import { ArrowsExpandIcon, ChevronLeftIcon, PlusIcon, XIcon } from '@heroicons/react/outline';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { useLocation, useParams } from 'react-router-dom';
import useSWRImmutable from "swr/immutable";
import { OAuthError, getData, postData, useResource } from '../backend';
import { useModal } from '../contexts';
import useUser from "../hooks/useUser";
import { Template } from "../interfaces";
import { BLOCK_ICON_MAP, BLOCK_NAME_MAP } from "../utils";
import ErrorModal from "./ErrorModal";
import LoadingModal from "./LoadingModal";
import OAuthModal from "./OAuthModal";
import OAuthModalHubspot from "./OAuthModalHubspot";
import TemplateListCard from "./TemplateListCard";

function TemplateDetail() {
  const { user } = useUser();
  const { t } = useTranslation();

  const templateId = useParams<{ id: string }>().id;
  const template: Template | null = useResource(`/templates/${templateId}`);
  const { data: templates } = useSWRImmutable<Array<Template>>("/templates/", getData);

  const { hasPermission } = useUser();

  const { openModal, closeModal } = useModal();
  const history = useHistory();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const auto = searchParams.get("auto") !== null;

  const [hasAutoRun, setHasAutoRun] = useState(false);
  const [iframeLocked, setIframeLocked] = useState(true);
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const [previewHovered, setPreviewHovered] = useState(false);
  const previewContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    document.body.style.overflow = previewExpanded ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [previewExpanded]);

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      const container = previewContainerRef.current;
      if (!container || iframeLocked)
        return;
      if (!container.contains(event.target as Node)) {
        setIframeLocked(true);
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);
    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
    };
  }, [iframeLocked]);

  useEffect(() => {
    const pageTitle = `${template?.name} | Portant`;
    if (template?.name && document.title !== pageTitle)
      document.title = pageTitle;

    return (() => { document.title = "Portant" })
  }, [template?.name])

  const createWorkflowRef = useCallback((node: any) => {
    if (!auto || user === undefined || !node || hasAutoRun) {
      return;
    }

    node.click();
    setHasAutoRun(true);
  }, [auto, user, hasAutoRun]);

  const relatedTemplates = useMemo(() => {
    if (!template || !templates?.length) return [];
    const categories = new Set(template.categories ?? []);
    return templates
      .filter((tpl) => tpl.id !== template.id && (tpl.categories ?? []).some((cat) => categories.has(cat)))
  }, [template, templates]);

  const embeddedUrl = useMemo(() => {
    const workflow = template?.workflow;
    if (!workflow?.previewFileUrl)
      return "";

    const appendQuery = (url: string) => (url.includes("?") ? `${url}&rm=minimal` : `${url}?rm=minimal`);

    if (workflow.previewDocumentType === "GOOGLE_SLIDES") {
      return appendQuery(workflow.previewFileUrl);
    }

    const basePreviewUrl = workflow.previewFileUrl.includes("/preview")
      ? workflow.previewFileUrl
      : workflow.previewFileUrl.replace("/edit", "/preview");

    return appendQuery(basePreviewUrl);
  }, [template?.workflow]);

  if (!template)
    return <div key="loading" className='m-auto'><img src="https://static.portant.co/portant-loading-light-gray.svg" className="w-32 h-32" alt="Loading Logo" /></div>

  async function handleCreateWorkflow() {
    openModal(<LoadingModal title={t("Creating Workflow from Template...")} subtitle={t("This may take some time as we create the template documents in your Google Drive.")} />);

    try {
      const workflow = await postData(`/templates/${template!.id}/duplicate/`);

      history.push(`/w/${workflow.id}?tour`);
      closeModal();

    } catch (error) {
      if (error instanceof OAuthError)
        if (error.oauthUrl.includes("app.hubspot"))
          openModal(<OAuthModalHubspot oauthUrl={error.oauthUrl} onClose={handleCreateWorkflow} />);
        else
          openModal(<OAuthModal oauthUrl={error.oauthUrl} onClose={handleCreateWorkflow} />)
      else
        openModal(<ErrorModal details={error} />)
    }
  }

  return (

    <div className="rounded-lg border border-gray-200 bg-white px-8 py-8 flex flex-col gap-6 pb-12">
      <div className="flex items-center justify-between">
        <button
          className="inline-flex items-center gap-2 text-gray-500 text-sm font-semibold hover:text-blue transition"
          onClick={() => history.push("/templates")}
        >
          <ChevronLeftIcon className="h-4 w-4" />
          {t("All templates")}
        </button>
      </div>

      <div className="flex items-end">

        <div className="flex flex-col gap-2">
          <h1 className="text-[32px] leading-[38px] font-gilroy font-semibold text-gray-900">
            {template.name}
          </h1>
          <p className="text-gray-500 text-base">{template.shortDescription}</p>
        </div>
        <button
          id="copy-template-button"
          className='btn btn-blue text-sm py-2 px-4 ml-auto'
          ref={createWorkflowRef}
          onClick={handleCreateWorkflow}
          disabled={!hasPermission(user?.activeTeam, "workflows.create")}
        >
          <PlusIcon className='w-4 h-4 mr-2' /> {t("Use template")}
        </button>
      </div>

      <section className="">
        <div className="relative w-full">
          <div
            ref={previewContainerRef}
            className={`relative rounded-lg transition-all ${previewExpanded ? "opacity-0 pointer-events-none" : iframeLocked ? (previewHovered ? "outline outline-2 outline-gray-300 border border-gray-200" : "outline outline-1 outline-transparent border border-gray-200") : "outline outline-2 outline-blue border border-gray-200"}`}
            onMouseEnter={() => setPreviewHovered(true)}
            onMouseLeave={() => setPreviewHovered(false)}
            onClick={() => {
              if (iframeLocked) setIframeLocked(false);
            }}
          >
            <div className="relative rounded-lg bg-white overflow-hidden flex items-center justify-center">
              <div className="absolute top-4 right-4 flex gap-2">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-600"
                  aria-pressed={previewExpanded}
                  onClick={() => setPreviewExpanded((prev) => !prev)}
                >
                  {previewExpanded ? (
                    <>
                      <span>{t("Close")}</span>
                      <XIcon className="h-4 w-4" />
                    </>
                  ) : (
                    <>
                      <span>{t("Fullscreen")}</span>
                      <ArrowsExpandIcon className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
              {!previewExpanded && embeddedUrl ? (
                <div
                  className="iFrame w-full h-[520px]"
                  style={{ pointerEvents: iframeLocked ? "none" : "auto" }}
                >
                  <iframe title={t("Goolge File Preview")} className="w-full h-full" src={embeddedUrl} />
                </div>
              ) : !previewExpanded ? (
                <div className="text-gray-500">{t("Preview Unavailable")}</div>
              ) : null}
            </div>
            <div
              className={`pointer-events-none absolute left-1/2 bottom-[1rem] mt-2 -translate-x-1/2 rounded-md bg-gray-900 px-4 py-2 text-xs font-semibold text-white transition duration-200 ease-out ${iframeLocked && previewHovered && !previewExpanded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1"
                }`}
            >
              {t("Click to preview the full document")}
            </div>
          </div>
        </div>
        {previewExpanded && <div className="h-[520px]" aria-hidden="true" />}
      </section>

      {previewExpanded && createPortal(
        <div className="fixed inset-0 z-50 bg-white">
          <div className="relative flex h-full w-full items-center justify-center">
            <div className="absolute top-6 right-6 z-10 flex gap-2">
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white/90 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:border-gray-400"
                aria-pressed="true"
                onClick={() => setPreviewExpanded(false)}
              >
                <span>{t("Close")}</span>
                <XIcon className="h-4 w-4" />
              </button>
            </div>
            <div className="relative h-full w-full bg-white">
              {embeddedUrl ? (
                <div className="iFrame w-full h-full" style={{ pointerEvents: "auto" }}>
                  <iframe title={t("Goolge File Preview")} className="w-full h-full" src={embeddedUrl} />
                </div>
              ) : (
                <div className="flex h-full w-full items-center justify-center text-gray-500">
                  {t("Preview Unavailable")}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      <section className="grid grid-cols-1 lg:grid-cols-[3fr_1fr] gap-8">
        <div className="p-6 flex flex-col gap-4">
          <h2 className="text-[20px] font-gilroy font-semibold text-gray-900">
            {t("Auto-generate NDAs and eSignature via Google Forms")}
          </h2>
          <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">{template.description}</p>
        </div>
        <div className="flex flex-col gap-6">
          <div className="p-6">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-[0.1em] mb-4">{t("Integrations")}</h3>
            <div className="flex flex-wrap gap-2">
              {["previewSourceType", "previewDocumentType"].map((key) => {
                const type = template.workflow?.[key as "previewSourceType" | "previewDocumentType"];
                if (!type) return null;
                return (
                  <div key={key} className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-3 py-1">
                    <img className="w-4 h-4" src={BLOCK_ICON_MAP[type]} alt={type} />
                    <span className="text-sm text-gray-600 capitalize">{BLOCK_NAME_MAP[type]}</span>
                  </div>
                );
              })}
              {template.workflow.hasSignatureRequest &&
                <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-3 py-1 text-nowrap flex-shrink-0">
                  <img className="w-4 h-4" src={BLOCK_ICON_MAP["SIGNATURE_REQUEST"]} alt={"SIGNATURE_REQUEST"} />
                  <span className="text-sm text-gray-600 capitalize -mb-0.5">{t("Request Signatures")}</span>
                </div>
              }
            </div>
          </div>

          <div className="p-6">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-[0.1em] mb-4">{t("Categories")}</h3>
            <div className="flex flex-wrap gap-2">
              {(template.categories ?? []).map((category) => (
                <span key={category} className="inline-flex rounded-full border border-gray-200 px-3 py-1 text-sm text-gray-600">
                  {category}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>
      <div className="h-px bg-gray-200" />

      {relatedTemplates.length > 0 && (
        <section className="flex flex-col gap-4 p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-[24px] leading-[28px] font-gilroy font-semibold text-gray-900">
                {t("Related Templates")}
              </h3>
            </div>
          </div>
          <div className="flex items-stretch overflow-x-scroll scrollbar-hidden gap-6 -mx-12 px-8 pr-28" style={{ width: "calc(100vw - 270px)" }}>
            {relatedTemplates.map((tpl) => (
              <TemplateListCard key={tpl.id} template={tpl} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export default TemplateDetail;
